import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, input, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { EventData } from '../../models/event-data';
import { Router } from '@angular/router';

import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { CommonModule } from '@angular/common';
import { EventService } from '../../services/event-service';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { EventModalComponent } from '../event-modal/event-modal.component';
import { Site } from '../../../../shared/models/site';
import { FonctionsService } from '../../../../shared/services/utilities/fonctions.service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../../../../shared/services/utilities/local-storage.service';
import { TransformedCoords } from '../../../../shared/models/transformed-coords';
import { ReportService, ControlPointInfoDto } from '../../../../shared/services/report/report.service';
import { CurrentStateService } from '../../../../core/services/current-state.service';
import { VibrationsRecordService } from '../../../../shared/services/records/vibrations-record.service';
import { VibrationsChart } from '../../../../shared/models/charts/VibrationsChart';
import { ReportControlPointSelectionModalComponent, ReportGenerationParams } from '../../../report/report-control-point-selection-modal/report-control-point-selection-modal.component';
import { ControlPoint } from '../../../../shared/models/controlPoint';
import { ControlPointsService } from '../../../control-points/services/control-points-service';


@Component({
  selector: 'app-event-detail',
  imports: [MatIconModule, CommonModule, TranslateModule],
  templateUrl: './event-detail.component.html',
  styleUrl: './event-detail.component.scss',
})
export class EventDetailComponent implements OnInit {
  @Input() event: EventData;
  @Input() site: Site;
  transformedCoords: TransformedCoords = { x: 0, y: 0 };
  controlPointId: number;
  @Input() vibrationRecords: VibrationsChart[];
  ControlPoint: ControlPoint;
  controlPoints?: ControlPoint[];

  constructor(
    private router: Router,
    private eventService: EventService,
    private dialogConfirme: MatDialog,
    private fonctionsService: FonctionsService,
    private reportService: ReportService,
    private currentStateService: CurrentStateService,
    private vibrationsRecordService: VibrationsRecordService,
    private controlPointsHttpService: ControlPointsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {
  }

  @Output() callLoadAllRecords = new EventEmitter<void>();

  ngOnInit(): void {
    this.transformedCoords = this.localStorageService.getItem('event-transformedCoords');
    setTimeout(() => {
      this.loadControlPoints();
    }, 500);

  }

  loadControlPoints(): void {
    this.controlPointsHttpService.getControlPointsBySiteId(this.site?.siteId!, false).subscribe(response => {
      this.controlPoints = response;
    })
  }
  backToEventList(): void {
    this.router.navigate(['/'], { queryParams: { eventId: this.event.id, siteId: this.site?.siteId } });
  }

  public onModifyVibrationEvent() {
    this.event.positionCoordinates.x = this.transformedCoords.x;
    this.event.positionCoordinates.y = this.transformedCoords.y;

    const dialogRef = this.dialogConfirme.open(EventModalComponent,
      {
        data: { currentEvent: this.event, currentSite: this.site },
        enterAnimationDuration: '0ms',
      },

    );

    dialogRef.afterClosed().subscribe(() => {
      this.loadCurrentEvent();
    });
  }

  loadCurrentEvent(): void {
    this.eventService.getVibrationEvent(this.site.siteId, this.event.id).subscribe({
      next: (response) => {
        this.event = response.data;
      },
      error: (err) => {
        console.error('Error loading source detail', err);
      }
    });
  }

  deleteVibrationEvent() {
    this.eventService.deleteVibrationEvent(this.site.siteId, this.event.id).subscribe({
      next: (response) => {
        this.router.navigate(['/'], { queryParams: { eventId: this.event.id, siteId: this.site.siteId } });
        this.fonctionsService.launch_toast(true, 'Source successfully deleted');
      },
      error: (err) => {
        this.fonctionsService.launch_toast(false, 'Error deleting source');
      }
    })
  }

  public onDelete() {
    const dialogRef = this.dialogConfirme.open(ConfirmDialogComponent, {
      enterAnimationDuration: '0ms',
      data: { title: 'Delete Source', message: 'Are you sure you want to delete this source?' },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteVibrationEvent();
      }
    });
  }
  downloadReport() {
    const dialogRef = this.dialogConfirme.open(ReportControlPointSelectionModalComponent, {
      width: '450px',
      disableClose: true,
      autoFocus: false,
      data: {
        siteId: this.site?.siteId || this.localStorageService.getItem('currentSiteID'),
        controlPoints: this.controlPoints || [],
        eventId: this.event?.id,
        eventName: this.event?.name,
        eventType: this.event?.eventTypeLabel,
        location: undefined,
        organisation: undefined
      }
    });

    dialogRef.afterClosed().subscribe((params: ReportGenerationParams) => {
      if (params) {
        this.generateMultiRecordReport(params);
      }
    });
  }

  private generateMultiRecordReport(params: ReportGenerationParams): void {
    const controlPointsDto: ControlPointInfoDto[] = params.controlPoints.map(cp => ({
      ControlPointId: cp.ControlPointId,
      ControlPointName: cp.ControlPointName
    }));

    this.currentStateService.setReportGenerationInProgress(true);
    this.reportService.generateVibrationReport(
      undefined,
      params.startDate,
      params.endDate,
      params.ponderationId,
      params.eventName || this.event?.name,
      params.eventType || this.event?.eventTypeLabel,
      this.site?.nom,
      params.organisation,
      controlPointsDto,
      this.site?.siteId
    ).subscribe({
      next: (blob) => {
        this.downloadPDF(blob, `Report_MultiRecord_${new Date().toLocaleDateString('fr-FR')}.pdf`);
        this.currentStateService.setReportGenerationInProgress(false);
      },
      error: async (error) => {
        console.error('Error generating multi-record report:', error);
        const errorMessage = await this.extractErrorMessage(error);
        if (errorMessage) {
          this.fonctionsService.launch_toast(false, errorMessage);
        }
        this.currentStateService.setReportGenerationInProgress(false);
      }
    });
  }

  private downloadPDF(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private async extractErrorMessage(error: any): Promise<string | null> {
    if (error?.error instanceof Blob) {
      try {
        const text = await error.error.text();
        const jsonError = JSON.parse(text);
        return jsonError.message || null;
      } catch (e) {
        return null;
      }
    }
    return error?.error?.message || error?.message || null;
  }
}
